import React, { useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { Button, Descriptions, Image, Space, Table, Tag, Typography } from 'antd';
import { EditOutlined, ExportOutlined, LoadingOutlined } from '@ant-design/icons';
import { routeHistory } from '@nackle/biw-web-router';
import { Routes } from '../../../routes';
import { MISSING_IMAGE } from '../../consts';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import { renderWithSearchWords } from '../../components/SearchBar/SearchBar';
import useDeleteCourse from '../../gql/hooks/useDeleteCourse'; 
import SearchQueryList from '../../components/SearchQueryList/SearchQueryList';
import { useFeatureFlags } from '../../../featureFlags';
import { flags } from '../../../featureFlags/flags'; 
import { ColoredTag } from '../GenericCreateEdit/GenericElements';
import { DEFAULT_LOCALE_CODE } from '../../../const';
import { useMinimalCourseQuery } from '../../../gql/hooks/queries/useMinimalCoursesQuery';

const NewTabLink = ( { href, children } ) =>
    <Typography.Link href={ href } target='_blank' rel='noopener noreferrer' style={ { position: 'relative' } }>
        { children } <ExportOutlined style={ { fontSize: '0.6em', position: 'absolute', top: 0, right: '-12px' } } />
    </Typography.Link>;

const learningObjectColumns = [
    {
        key: 'name',
        dataIndex: 'name',
        title: 'Name',
        ellipsis: true,
        render: ( name, lo ) =>
            <NewTabLink href={ Routes.EditObject.buildPath( lo.id ) }>{ name }</NewTabLink>
    },
    {
        key: 'type',
        dataIndex: 'format',
        title: 'Type',
        width: '80px',
        align: 'center',
        render: ( format ) => format ?
            <ColoredTag style={ { marginInlineEnd: 0 } } value={ format }>{ format.toUpperCase() }</ColoredTag> :
            <span className='canceled'>&mdash;</span>
    }
];

const PREREQUISITE_SEARCH = {
    types: [ 'courses' ],
    query: '',
    page: 1,
    sort: 'name',
    order: 'asc',
    localeCode: 'en_US',
};

const CourseExpandedRow = ( { course, showILT } ) => {

    const [ sequentialAllowed ] = useFeatureFlags( [ flags.SEQUENTIAL_COURSES ] );
    
    const prerequisiteIds = course.prerequisites ?? [];
    const prerequisiteOfIds = course.prerequisiteOf ?? [];

    // both lists are looked up in one request, sent when the row is first expanded
    const idsKey = [ ...prerequisiteIds, ...prerequisiteOfIds ].join( ',' );
    const ids = useMemo( () => [ ...new Set( [ ...prerequisiteIds, ...prerequisiteOfIds ] ) ], [ idsKey ] );

    const { courses, loading } = useMinimalCourseQuery(
        { ...PREREQUISITE_SEARCH, limit: ids.length, _ids: ids },
        { fetchPolicy: 'cache-first', skip: ids.length === 0 }
    );

    const courseNames = useMemo( () => Object.fromEntries( courses.map( c => [ c.courseId, c.name ] ) ), [ courses ] );

    const renderCourseLinks = ( courseIds, emptyText ) => {
        if ( courseIds.length === 0 ) {
            return <span className='canceled'>{ emptyText }</span>;
        }
        if ( loading ) {
            return <LoadingOutlined spin />;
        }
        return courseIds.map( id => <div key={ id }>
            { courseNames[ id ] ?
                <NewTabLink href={ Routes.EditCourse.buildPath( id ) }>{ courseNames[ id ] }</NewTabLink> :
                <span className='canceled'>Unknown course</span> }
        </div> );
    };

    return (
        <div className="row-expand">
            <Descriptions bordered layout='vertical' items={ [
                {
                    key: 'description',
                    label: `Description (${ DEFAULT_LOCALE_CODE })`,
                    span: 3,
                    children: course.content?.length > 0 ?
                        <Typography.Paragraph ellipsis={ { rows: 6 } }>
                            <span dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize( course.content ) } } />
                        </Typography.Paragraph> :
                        <span className='canceled'>No description</span>
                },
                // the search has no session details for ILT courses, so they leave this out
                ...( showILT && course.isILT ? [] : [ {
                    key: 'learningObjects',
                    label: 'Learning Objects',
                    span: 3,
                    children: course.learningObjects?.length > 0 ?
                        <>
                            <Table
                                size='small'
                                bordered
                                rowKey='id'
                                columns={ learningObjectColumns }
                                dataSource={ course.learningObjects }
                                pagination={ false }
                            />
                            { !course.isPublished && <span className='canceled'>Changes in learning objects may not show accurately here until course is published.</span> }
                        </> : <span className='canceled'>No learning objects</span>
                } ] ),
                {
                    key: 'featured',
                    label: 'Featured',
                    children: course.isFeatured ?
                        <ColoredTag.Featured isFeatured /> :
                        <span className='canceled'>Not featured</span>
                },
                ...( sequentialAllowed ? [ {
                    key: 'sequential',
                    label: 'Sequential',
                    children: <ColoredTag.Sequential isSequential={ course.isSequential } />
                } ] : [] ),
                {
                    key: 'tags',
                    label: 'Tags',
                    children: course.tags?.length > 0 ?
                        course.tags.map( t => <ColoredTag key={ t.name } value={ t.name } /> ) :
                        <span className='canceled'>No tags</span>
                },
                {
                    key: 'prerequisiteOf',
                    label: 'Prerequisite Of',
                    children: renderCourseLinks( prerequisiteOfIds, 'Not a prerequisite' )
                },
                {
                    key: 'prerequisites',
                    label: 'Prerequisites',
                    children: <>
                        { prerequisiteIds.length > 1 &&
                            <Typography.Text type='secondary'>
                                { course.prerequisiteOperator === 'AND' ? 'All required' : 'Any one required' }
                            </Typography.Text> }
                        { renderCourseLinks( prerequisiteIds, 'No prerequisites' ) }
                    </>
                },
            ] } />
        </div>
    );
};

let expandedRows = [];

const CoursesListPage = () => { 
    const listPageRef = useRef( null );
    const { deleteCourse, isDeleting } = useDeleteCourse();
    const [ showBrands ] = useFeatureFlags( [ flags.BRANDS ] );
    const [ showILT ] = useFeatureFlags( [ flags.ILT_ENABLED ] );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Learning Content', path: Routes.LearningContentNav.buildPath() },
        { name: 'Courses' }
    ];

    const uiStrings = {
        cardTitle: 'Courses',
        createButtonTitle: 'Create New Course'
    };

    const handleDeleteClick = async ( id ) => {
        await deleteCourse( id, () => {
            listPageRef.current.reload();
        } );
    };

    const handleCreateClick = () => {
        routeHistory.push( Routes.CreateCourse.buildPath() );
    };

    const handleSetExpandedRows = ( rows ) => {
        expandedRows = rows;
    };

    const renderExpandedRow = ( course ) => <CourseExpandedRow course={ course } showILT={ showILT } />;

    const tableColumns = [
        {
            dataIndex: 'thumbnailImage',
            key: 'thumbnailImage', 
            title: 'Thumbnail',
            width: 120,
            fixed: 'left',
            render: ( thumbnailImage ) => 
                <Image 
                    width="100px"
                    className="thumbnail-image" 
                    fallback={ MISSING_IMAGE }
                    src={ thumbnailImage || MISSING_IMAGE }
                    preview={ !!thumbnailImage }
                />
        },
        {
            dataIndex: 'name',
            key: 'name', 
            title: 'Name',
            sorter: ( a, b ) => 0,
            sortKey: 'name',
            width: 350,
            fixed: 'left',
            render: ( name, i ) => {
                if ( !i.content && !i.name ) {
                    return <span className="no-translation">No translation for this language</span>;
                } else {
                    return (
                        <span className="name-column">
                            { renderWithSearchWords( name, listPageRef.current.getSearchWords() ) }
                        </span> 
                    );
                }
            }
        },
        ...( showILT ?
            [
                {
                    dataIndex: 'isILT',
                    key: 'isILT',
                    title: 'ILT',
                    render: ( i ) => i ? <Tag color="blue">ILT</Tag> : ''
                },
            ] : [] ),
        ...( showBrands ?
            [
                {
                    dataIndex: 'brandIds',
                    key: 'brands',
                    title: 'Brand',
                    sortKey: 'brands',
                    render: ( b ) => {
                        return b.map( i => {
                            const brand = listPageRef.current.getBrand( i );
                            return brand ? <ColoredTag.Brand key={ brand.id } brand={ brand } /> : null;
                        } );
                    }
                },
            ] : [] ),
        {
            dataIndex: 'isPublished',
            key: 'isPublished',
            title: 'Published',
            render: ( isPublished ) => <ColoredTag.Publish isPublished={ isPublished } />
        },
        {
            dataIndex: 'createdAt',
            key: 'createdAt',
            title: 'Created At',
            sorter: ( a, b ) => 0,
            sortKey: 'createdAt',
            render: ( createdAt ) => <span>{ createdAt }</span>
        },
        {
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            title: 'Updated At',
            sorter: ( a, b ) => 0,
            sortKey: 'updatedAt',
            render: ( updatedAt ) => <span>{ updatedAt }</span>
        },
        {
            title: 'Actions',
            key: 'actions', 
            fixed: 'right', 
            width: '100px',
            render: ( item ) => {
                return (
                    <>
                        <Space>
                            <Button 
                                type="link" 
                                icon={ <EditOutlined /> } 
                                onClick= { () => routeHistory.push( Routes.EditCourse.buildPath( item.id ) ) }
                            />
                            
                            <DeleteButton
                                id={ item.id }   
                                title={ 'Confirm Delete' }
                                disabled={ item.prerequisiteOf && item.prerequisiteOf.length > 0 }
                                deleteCallback={ ( i ) => handleDeleteClick( i ) }
                                confirmMessage= { 
                                    <>
                                        <p>Are you <i>sure</i> you want to permanently delete the course <b>{ item.name }</b>? 
                                        This action cannot be undone.</p>
                                    </>
                                }
                            />
                        </Space>
                    </> 
                );
            }   
        }
    ];   
  
    return (
        <>
            <SearchQueryList 
                ref={ listPageRef }
                type="courses" 
                uiStrings={ uiStrings }
                breadcrumbs={ breadcrumbs }
                tableColumns={ tableColumns }
                isDeleting={ isDeleting }
                renderExpandedRowCallback= { renderExpandedRow }
                createClickCallback={ handleCreateClick }
                setExpandedRowsCallback= { handleSetExpandedRows }
                defaultExpandedRows= { expandedRows }
            />
        </>
    );
};


export default CoursesListPage;
